/**
 * /payment/success — post-Stripe-checkout landing for memorial payments.
 * Polls /api/memorials/checkout/status/{sid} until paid, then shows a
 * warm confirmation and a "Back to memorial" link.
 */
import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { http } from "@/lib/api";
import { CheckCircle2, Loader2, AlertCircle, Heart } from "lucide-react";

const POLL_INTERVAL = 2000;
const MAX_ATTEMPTS = 30; // 60s total

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sid = params.get("session_id");
  const [state, setState] = useState({ status: "polling", data: null, error: null, attempts: 0 });
  const timerRef = useRef(null);

  useEffect(() => {
    if (!sid) {
      setState({ status: "error", error: "Missing session id.", data: null, attempts: 0 });
      return undefined;
    }

    let cancelled = false;
    let attempts = 0;

    const poll = async () => {
      attempts += 1;
      try {
        const { data } = await http.get(`/memorials/checkout/status/${sid}`);
        if (cancelled) return;
        if (data.payment_status === "paid") {
          setState({ status: "paid", data, error: null, attempts });
          return;
        }
        if (attempts >= MAX_ATTEMPTS) {
          setState({ status: "timeout", data, error: null, attempts });
          return;
        }
        setState({ status: "polling", data, error: null, attempts });
        timerRef.current = setTimeout(poll, POLL_INTERVAL);
      } catch (e) {
        if (cancelled) return;
        setState({ status: "error", error: e?.response?.data?.detail || "Couldn't fetch payment status.", data: null, attempts });
      }
    };

    poll();
    return () => {
      cancelled = true;
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [sid]);

  const memorialLink = state.data?.memorial_slug ? `/celebrate/${state.data.memorial_slug}` : "/celebrate";

  return (
    <div className="max-w-lg mx-auto px-6 py-16" data-testid="payment-success-page">
      {state.status === "polling" && (
        <div className="wofy-card text-center py-14" data-testid="payment-status-polling">
          <Loader2 className="w-8 h-8 animate-spin mx-auto" style={{ color: "var(--wofy-primary)" }} />
          <h1 className="font-display text-2xl mt-4">Finalising your payment…</h1>
          <p className="text-sm mt-2" style={{ color: "var(--wofy-text-soft)" }}>
            This usually takes only a few seconds.
          </p>
        </div>
      )}

      {state.status === "paid" && (
        <div className="wofy-card text-center py-12" data-testid="payment-status-paid">
          <CheckCircle2 className="w-12 h-12 mx-auto" style={{ color: "#16a34a" }} />
          <h1 className="font-display text-3xl mt-4">Thank you.</h1>
          <p className="text-base mt-3">
            {kindMessage(state.data)}
          </p>
          {state.data?.donor_message && (
            <blockquote className="italic mt-4 text-sm p-3 rounded-lg" style={{ background: "rgba(178,93,56,0.06)", color: "var(--wofy-text)" }}>
              "{state.data.donor_message}"
            </blockquote>
          )}
          <div className="mt-6 flex flex-col sm:flex-row gap-2 items-center justify-center">
            <Link to={memorialLink} className="wofy-btn-primary" data-testid="payment-success-back-btn">
              <Heart className="w-4 h-4" /> Back to memorial
            </Link>
            <Link to="/celebrate" className="wofy-btn-ghost text-sm" data-testid="payment-success-browse-btn">
              Browse memorials
            </Link>
          </div>
          <p className="text-[10px] mt-6 opacity-60">
            A receipt has been emailed to you by Stripe. Your tribute is now visible on the memorial's wall.
          </p>
        </div>
      )}

      {state.status === "timeout" && (
        <div className="wofy-card text-center py-12" data-testid="payment-status-timeout">
          <AlertCircle className="w-10 h-10 mx-auto" style={{ color: "#f59e0b" }} />
          <h1 className="font-display text-2xl mt-3">Still processing…</h1>
          <p className="text-sm mt-2" style={{ color: "var(--wofy-text-soft)" }}>
            Your payment is with Stripe. You'll see it on the memorial once it clears (usually within a minute).
          </p>
          <Link to={memorialLink} className="wofy-btn-primary mt-6 inline-flex">
            <Heart className="w-4 h-4" /> Back to memorial
          </Link>
        </div>
      )}

      {state.status === "error" && (
        <div className="wofy-card text-center py-12" data-testid="payment-status-error">
          <AlertCircle className="w-10 h-10 mx-auto" style={{ color: "#dc2626" }} />
          <h1 className="font-display text-2xl mt-3">We couldn't confirm your payment.</h1>
          <p className="text-sm mt-2" style={{ color: "var(--wofy-text-soft)" }}>{state.error}</p>
          <Link to="/celebrate" className="wofy-btn-ghost mt-6 inline-flex">Back to memorials</Link>
        </div>
      )}
    </div>
  );
}

function kindMessage(data) {
  const label = data?.label || "your tribute";
  switch (data?.kind) {
    case "candle":     return `Your candle is lit in loving memory. Thank you for taking a moment.`;
    case "flowers":    return `Your flowers have been placed on the memorial. Beautiful gesture.`;
    case "donation":   return `Your donation is received with gratitude. Every dollar keeps the memorial alive.`;
    case "photobook":  return `Your printed photo book will be shipped within 7-10 days.`;
    case "livestream": return `Your livestream package is confirmed. The family has been notified.`;
    case "postcards":  return `Your yearly anniversary postcard subscription is active.`;
    default:           return `We've received "${label}" for this memorial. Thank you.`;
  }
}
